<template>
    <v-layout align-center justify-center>
        <v-progress-linear indeterminate fixed v-if="loading" style="top:0"/>
        <v-flex xs12 sm9 md6 lg4 class="py-4">
            <v-img :src="'/img/logo-valzum.png'" class="px-6" max-height="120" contain/>
            <v-card flat color="transparent" class="mx-3">
                <v-toolbar flat :color="'grey ' + (!$vuetify.theme.dark ? 'light' : 'dark') + 'en-4'" class="mb-4">
                    <v-toolbar-title
                        class="text-center font-weight-bold primary--text text-subtitle-1 text-sm-h5"
                        style="white-space: normal; width: 100%">CREAR CUENTA
                    </v-toolbar-title>
                    <div class="flex-grow-1"></div>
                </v-toolbar>
                <oauth-buttons/>
                <v-card-text class="text-center subtitle-1 py-2 pb-0">-o- Escribe tus Datos</v-card-text>
            </v-card>
            <v-card flat color="transparent" class="ma-3">
                <v-form ref="form" @submit.prevent="submit" lazy-validation v-model="valid">
                    <v-card-text class="px-0 pb-0">
                        <div class="register-steps mb-4">
                            <div
                                v-for="item in stepItems"
                                :key="item.value"
                                class="register-step"
                                :class="stepClass(item.value)">
                                <v-avatar
                                    :color="step >= item.value ? 'primary' : ($vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-3')"
                                    size="30"
                                    class="register-step-avatar">
                                    <v-icon v-if="step > item.value" small color="secondary">mdi-check</v-icon>
                                    <span v-else class="caption font-weight-bold" :class="step >= item.value ? 'secondary--text' : 'grey--text'">
                                        {{ item.value }}
                                    </span>
                                </v-avatar>
                                <span class="register-step-label caption font-weight-bold">
                                    {{ item.label }}
                                </span>
                            </div>
                        </div>
                        <v-progress-linear :value="stepProgress" color="primary" height="4" rounded class="mb-5"/>

                        <div v-show="step === 1">
                            <v-select
                                :label="labels.country"
                                :items="items"
                                name="country"
                                v-model="form.country"
                                :error-messages="errors.country"
                                :disabled="loading"
                                :rules="[rules.required('country')]"
                                @change="clearErrors('country')"
                                prepend-inner-icon="mdi-map-marker"
                                item-text="name"
                                item-value="code"
                                filled>
                                <template v-slot:selection="{ item }">
                                    <v-flex>
                                        <v-avatar size="28px" class="mr-2">
                                            <img :src="'https://flagcdn.com/w1280/' + (item.code).toLowerCase() + '.png'" :alt="item.name.toLowerCase()" />
                                        </v-avatar>{{ item.name }}
                                    </v-flex>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-avatar>
                                        <img :src="'https://flagcdn.com/w1280/' + (item.code).toLowerCase() + '.png'" :alt="item.code.toLowerCase()" />
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title v-html="item.name"/>
                                    </v-list-item-content>
                                </template>
                            </v-select>
                            <v-text-field
                                :label="labels.name"
                                type="text"
                                name="name"
                                v-model="form.name"
                                :error-messages="errors.name"
                                :rules="[rules.required('name'), rules.max('name', 160, 'El')]"
                                :disabled="loading"
                                @input="onNameInput"
                                prepend-inner-icon="mdi-account-circle"
                                filled/>
                            <v-text-field
                                :label="labels.lname"
                                type="text"
                                name="lname"
                                v-model="form.lname"
                                :error-messages="errors.lname"
                                :rules="[rules.required('lname'), rules.max('lname', 160, 'El')]"
                                :disabled="loading"
                                @input="onLastNameInput"
                                prepend-inner-icon="mdi-account"
                                filled/>
                            <v-card-actions class="register-actions px-0">
                                <v-spacer/>
                                <v-btn color="primary" class="font-weight-bold" :block="$vuetify.breakpoint.xsOnly" :disabled="loading" @click="nextStep">
                                    Continuar
                                    <v-icon right>mdi-arrow-right</v-icon>
                                </v-btn>
                            </v-card-actions>
                        </div>

                        <div v-show="step === 2">
                            <v-text-field
                                :label="labels.nickname"
                                type="text"
                                name="nickname"
                                v-model="form.nickname"
                                :error-messages="errors.nickname"
                                :rules="[validateNickname]"
                                :disabled="loading"
                                prefix="$"
                                :success="nickname.available === true"
                                :error="nickname.available === false"
                                :messages="nickname.message"
                                @input="onNicknameInput"
                                prepend-inner-icon="mdi-at"
                                filled/>
                            <div class="mb-3" v-if="nickname.suggestions.length">
                                <v-chip
                                    v-for="item in nickname.suggestions"
                                    :key="item"
                                    small
                                    color="secondary"
                                    class="primary--text font-weight-bold mr-1 mb-1"
                                    @click="selectNickname(item)">
                                    ${{ item }}
                                </v-chip>
                            </div>
                            <v-text-field
                                :label="labels.phone"
                                name="phone"
                                type="tel"
                                inputmode="tel"
                                v-model="form.phone"
                                :error-messages="errors.phone"
                                :rules="[rules.required('phone'), rules.max('phone', 255, 'El')]"
                                :disabled="loading"
                                @input="clearErrors('phone')"
                                prepend-inner-icon="mdi-cellphone"
                                hint="Ejemplo: +51960193293"
                                filled/>
                            <v-text-field
                                :label="labels.email"
                                type="email"
                                name="email"
                                inputmode="email"
                                v-model="form.email"
                                :error-messages="errors.email"
                                :rules="[rules.required('email'), rules.mail('email')]"
                                :disabled="loading"
                                @input="clearErrors('email')"
                                prepend-inner-icon="mdi-email"
                                filled/>
                            <v-card-actions class="register-actions px-0">
                                <v-btn text color="primary" :disabled="loading" @click="previousStep">
                                    <v-icon left>mdi-arrow-left</v-icon>
                                    Atrás
                                </v-btn>
                                <v-spacer/>
                                <v-btn color="primary" class="font-weight-bold" :block="$vuetify.breakpoint.xsOnly" :disabled="loading" @click="nextStep">
                                    Continuar
                                    <v-icon right>mdi-arrow-right</v-icon>
                                </v-btn>
                            </v-card-actions>
                        </div>

                        <div v-show="step === 3">
                            <v-text-field
                                :label="labels.password"
                                name="password"
                                v-model="form.password"
                                :append-icon="passwordHidden ? 'mdi-eye-off' : 'mdi-eye'"
                                @click:append="() => (passwordHidden = !passwordHidden)"
                                :type="passwordHidden ? 'password' : 'text'"
                                :error-messages="errors.password"
                                :disabled="loading"
                                :rules="[rules.req('password'), rules.min('password', 8, 'La')]"
                                hint="Mínimo 8 caracteres"
                                @input="clearErrors('password')"
                                prepend-inner-icon="mdi-lock"
                                filled/>
                            <v-text-field
                                :label="labels.password_confirmation"
                                name="password_confirmation"
                                v-model="form.password_confirmation"
                                :append-icon="passwordHidden ? 'mdi-eye-off' : 'mdi-eye'"
                                @click:append="() => (passwordHidden = !passwordHidden)"
                                :type="passwordHidden ? 'password' : 'text'"
                                :error-messages="errors.password_confirmation"
                                :disabled="loading"
                                :rules="[rules.req('password_conf'), passwordMatches]"
                                @input="clearErrors('password_confirmation')"
                                prepend-inner-icon="mdi-lock"
                                filled/>
                            <v-switch name="terms" v-model="form.terms" :disabled="loading" class="mt-0"
                                      :rules="[rules.cond('terms')]" :error-messages="errors.terms"
                                      @input="clearErrors('terms')" color="success" readonly @click="dialog = true">
                                <template v-slot:label>
                                    <div><strong @click="dialog = true">Leer y Aceptar los {{ labels.terms }}</strong></div>
                                </template>
                            </v-switch>
                            <v-card-actions class="register-actions px-0">
                                <v-btn text color="primary" :disabled="loading" @click="previousStep">
                                    <v-icon left>mdi-arrow-left</v-icon>
                                    Atrás
                                </v-btn>
                                <v-spacer/>
                                <v-btn
                                    type="submit"
                                    :loading="loading"
                                    :disabled="loading"
                                    color="primary"
                                    class="font-weight-bold"
                                    :block="$vuetify.breakpoint.xsOnly"
                                    large>
                                    Crear Cuenta
                                </v-btn>
                            </v-card-actions>
                        </div>
                        <input
                            v-model="form.website_url"
                            class="auth-hp-field"
                            tabindex="-1"
                            autocomplete="off"
                            name="website_url"
                            type="text"
                            aria-hidden="true">
                    </v-card-text>
                </v-form>
            </v-card>
            <div class="text-center py-2 py-sm-4 hidden-xs-only">
                ¿Ya tienes una cuenta?&nbsp;&nbsp;
                <v-btn color="secondary primary--text" to="/login">Ingresar</v-btn>
            </div>
            <v-dialog v-model="dialog" persistent scrollable width="800px">
                <v-card>
                    <v-card-title class="text-center headline primary d-inline-block" primary-title style="color: #fefefe; word-break: normal; width: 100%">{{labels.terms}}</v-card-title>
                    <v-card-text class="px-0">
                        <terms :style="'margin-top: -' + mrg + 'px'" :w="'100'"/>
                    </v-card-text>
                    <v-divider/>
                    <v-card-actions class="px-sm-8">
                        <v-btn color="error" large @click="[dialog = false, form.terms = false]">
                            <v-icon>mdi-close-circle</v-icon>&nbsp;&nbsp;No acepto
                        </v-btn>
                        <v-spacer/>
                        <v-btn color="success" large @click="[dialog = false, form.terms = true]">
                            <v-icon>mdi-check-circle</v-icon>&nbsp;&nbsp;Acepto
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-flex>
    </v-layout>
</template>

<script>
import axios from "axios";
import Form from "../../helpers/form";
import { isMobileOnly } from "mobile-device-detect";
import Terms from "../Terms";
import OAuthButtons from "./providers/OAuthButtons";

export default {
    name: "Register",
    components: { 'oauth-buttons': OAuthButtons, Terms },
    mixins: [ Form ],
    data: () => ({
        dialog: false,
        passwordHidden: true,
        step: 1,
        mrg: isMobileOnly ? '74' : '54',
        labels: {
            country: 'País',
            name: 'Nombres',
            lname: 'Apellidos',
            nickname: 'Nickname',
            phone: 'Teléfono',
            email: 'Correo Electrónico',
            password: 'Contraseña',
            password_conf: 'confirmación de la contraseña',
            password_confirmation: 'Confirmar Contraseña',
            terms: 'Términos y Condiciones'
        },
        form: {
            country: null,
            name: null,
            lname: null,
            nickname: null,
            phone: null,
            email: null,
            password: null,
            password_confirmation: null,
            terms: false,
            _auth_challenge: null,
            website_url: null
        },
        nickname: {
            available: null,
            message: '',
            suggestions: [],
            timer: null
        },
        items: [
            //{ name: 'Argentina', code: 'ARG' },
            { name: 'Chile', code: 'CHL' },
            { name: 'Colombia', code: 'COL' },
            //{ name: 'Ecuador', code: 'ECU' },
            { name: 'España', code: 'ESP' },
            //{ name: 'Mexico', code: 'MEX' },
            { name: 'Peru', code: 'PER' },
            { name: 'Venezuela', code: 'VEN' }
        ]
    }),
    computed: {
        stepItems() {
            return [
                { value: 1, label: 'Datos' },
                { value: 2, label: 'Contacto' },
                { value: 3, label: 'Seguridad' }
            ];
        },
        stepProgress() {
            return (this.step / this.stepItems.length) * 100;
        }
    },
    mounted() {
        this.form.country = this.$route.query.country || null;
        this.fetchChallenge('register');
    },
    methods: {
        async submit() {
            const validSteps = await this.validateAllSteps();

            if (!validSteps || !this.$refs.form.validate()) {
                return;
            }

            this.loading = true;

            if (!this.form._auth_challenge) {
                await this.fetchChallenge('register');
            }

            axios.post('/api/register', this.form).then((res) => {
                if (res.data.email_verification_required) {
                    this.$toast.info(res.data.message, {icon: 'mdi-email-check'});
                    this.$router.push({
                        name: 'Login',
                        query: {
                            email: res.data.email || this.form.email,
                            registered: 1
                        }
                    });
                } else {
                    this.$toast.success('¡Te has registrado exitosamente!', {icon: 'mdi-check-circle'});
                    this.success(res.data);
                }
            }).catch(err => {
                const data = err.response && err.response.data ? err.response.data : {};
                this.handleErrors(data.errors);
                this.focusErrorStep(data.errors || {});
            }).then(() => {
                this.fetchChallenge('register');
                this.loading = false;
            })
        },
        fetchChallenge(action) {
            return axios.get('/api/auth/challenge/' + action)
                .then(res => {
                    this.form._auth_challenge = res.data.token;
                })
                .catch(() => {
                    this.form._auth_challenge = null;
                });
        },
        onNameInput() {
            this.clearErrors('name');
            if (!this.form.nickname) {
                this.queueNicknameCheck();
            }
        },
        onLastNameInput() {
            this.clearErrors('lname');
            if (!this.form.nickname) {
                this.queueNicknameCheck();
            }
        },
        onNicknameInput() {
            this.clearErrors('nickname');
            this.queueNicknameCheck();
        },
        queueNicknameCheck() {
            clearTimeout(this.nickname.timer);
            this.nickname.timer = setTimeout(this.checkNickname, 400);
        },
        checkNickname() {
            return axios.post('/api/nickname/check', {
                nickname: this.form.nickname,
                name: this.form.name,
                lname: this.form.lname
            }).then(res => {
                this.nickname.suggestions = res.data.suggestions || [];

                if (!this.form.nickname && this.nickname.suggestions.length) {
                    this.nickname.available = null;
                    this.nickname.message = 'Puedes elegir una sugerencia o dejarlo vacio para recibir uno temporal.';
                    return;
                }

                this.form.nickname = res.data.nickname || this.form.nickname;
                if (!this.form.nickname) {
                    this.nickname.available = null;
                    this.nickname.message = '';
                } else if (!res.data.valid) {
                    this.nickname.available = false;
                    this.nickname.message = 'Usa 4 a 20 caracteres: letras, numeros o guion bajo.';
                } else if (res.data.available) {
                    this.nickname.available = true;
                    this.nickname.message = 'Nickname disponible.';
                } else {
                    this.nickname.available = false;
                    this.nickname.message = 'Ese nickname ya esta en uso.';
                }
            }).catch(() => {
                this.nickname.available = null;
                this.nickname.message = '';
            });
        },
        selectNickname(nickname) {
            this.form.nickname = nickname;
            this.checkNickname();
        },
        async nextStep() {
            this.clearStepErrors(this.step);

            if (this.step === 2 && this.form.nickname) {
                await this.checkNickname();
            }

            if (!this.validateStep(this.step)) {
                return;
            }

            this.step = Math.min(this.step + 1, 3);
        },
        previousStep() {
            this.step = Math.max(this.step - 1, 1);
        },
        async validateAllSteps() {
            this.clearErrors();

            if (this.form.nickname) {
                await this.checkNickname();
            }

            for (let targetStep = 1; targetStep <= 3; targetStep++) {
                if (!this.validateStep(targetStep)) {
                    this.step = targetStep;
                    return false;
                }
            }

            return true;
        },
        validateStep(step) {
            const errors = {};

            this.clearStepErrors(step);

            if (step === 1) {
                if (!this.filled(this.form.country)) {
                    errors.country = 'El pais es obligatorio.';
                }
                if (!this.filled(this.form.name)) {
                    errors.name = 'El nombre es obligatorio.';
                } else if (this.form.name.length > 160) {
                    errors.name = 'El nombre debe tener menos de 160 caracteres.';
                }
                if (!this.filled(this.form.lname)) {
                    errors.lname = 'El apellido es obligatorio.';
                } else if (this.form.lname.length > 160) {
                    errors.lname = 'El apellido debe tener menos de 160 caracteres.';
                }
            }

            if (step === 2) {
                if (this.form.nickname && !/^[A-Za-z0-9_]{4,20}$/.test(this.form.nickname)) {
                    errors.nickname = 'Usa 4 a 20 caracteres: letras, numeros o guion bajo.';
                } else if (this.nickname.available === false) {
                    errors.nickname = this.nickname.message || 'Ese nickname ya esta en uso.';
                }
                if (!this.filled(this.form.phone)) {
                    errors.phone = 'El telefono es obligatorio.';
                } else if (this.form.phone.length > 255) {
                    errors.phone = 'El telefono debe tener menos de 255 caracteres.';
                }
                if (!this.filled(this.form.email)) {
                    errors.email = 'El correo electronico es obligatorio.';
                } else if (!/.+@.+\..+/.test(this.form.email)) {
                    errors.email = 'El correo electronico no es valido.';
                }
            }

            if (step === 3) {
                if (!this.filled(this.form.password)) {
                    errors.password = 'La contrasena es obligatoria.';
                } else if (this.form.password.length < 8) {
                    errors.password = 'La contrasena debe tener mas de 8 caracteres.';
                }
                if (!this.filled(this.form.password_confirmation)) {
                    errors.password_confirmation = 'La confirmacion de la contrasena es obligatoria.';
                } else if (this.form.password_confirmation !== this.form.password) {
                    errors.password_confirmation = 'La confirmacion de la contrasena no coincide.';
                }
                if (!this.form.terms) {
                    errors.terms = 'Debes aceptar los terminos y condiciones.';
                }
            }

            Object.keys(errors).forEach(field => this.setLocalError(field, errors[field]));

            return !Object.keys(errors).length;
        },
        clearStepErrors(step) {
            this.stepFields(step).forEach(field => this.clearErrors(field));
        },
        stepFields(step) {
            if (step === 1) {
                return ['country', 'name', 'lname'];
            }
            if (step === 2) {
                return ['nickname', 'phone', 'email'];
            }
            return ['password', 'password_confirmation', 'terms'];
        },
        setLocalError(field, message) {
            this.$set(this.errors, field, [message]);
        },
        stepClass(value) {
            return {
                'register-step--active': this.step === value,
                'register-step--complete': this.step > value
            };
        },
        filled(value) {
            return value !== null && value !== undefined && String(value).trim() !== '';
        },
        focusErrorStep(errors) {
            const fields = Object.keys(errors);

            if (fields.some(field => ['country', 'name', 'lname'].includes(field))) {
                this.step = 1;
            } else if (fields.some(field => ['nickname', 'phone', 'email'].includes(field))) {
                this.step = 2;
            } else if (fields.some(field => ['password', 'password_confirmation', 'terms', '_auth_challenge', 'website_url'].includes(field))) {
                this.step = 3;
            }
        },
        passwordMatches(value) {
            return value === this.form.password || 'La confirmacion de la contrasena no coincide.';
        },
        validateNickname(value) {
            if (!value) return true;
            return /^[A-Za-z0-9_]{4,20}$/.test(value) || 'Usa 4 a 20 caracteres: letras, numeros o guion bajo.';
        },
        success(data) {
            this.$store.dispatch('saveToken', data);
            this.$store.dispatch('setUser', data);
            this.$router.push({ name: 'Home' });
        }
    }
}
</script>

<style scoped>
.register-steps {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.register-step {
    align-items: center;
    display: flex;
    min-width: 0;
    opacity: 0.65;
}

.register-step--active,
.register-step--complete {
    opacity: 1;
}

.register-step-avatar {
    flex: 0 0 auto;
}

.register-step-label {
    color: var(--v-disabled-base);
    line-height: 1.1;
    margin-left: 8px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.register-step--active .register-step-label,
.register-step--complete .register-step-label {
    color: var(--v-primary-base);
}

.register-actions {
    gap: 12px;
}

@media (max-width: 600px) {
    .register-steps {
        gap: 6px;
    }

    .register-step {
        justify-content: center;
    }

    .register-step-label {
        display: none;
    }

    .register-actions {
        align-items: stretch;
        flex-direction: column-reverse;
    }

    .register-actions .spacer {
        display: none;
    }

    .register-actions .v-btn {
        width: 100%;
    }
}
</style>
